import React, { useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { AxiosResponse } from 'axios';

import StandardDialog, { StandardDialogActions } from '../Modals/StandardDialog';
import { downloadBlobResponse } from '../DownloadReport/downloadUtils';
import { useAppSelector } from '../../app/hooks';

export default function ExportDownloadMenuItem({ label, fallbackFilename, fetchExport, onComplete }: {
  label: string;
  fallbackFilename: string;
  fetchExport: () => Promise<AxiosResponse<Blob>>;
  onComplete: () => void;
}) {
  const { user } = useAppSelector((state) => state.user);
  const [loading, setLoading] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const handleConfirmOpen = () => {
    setAgreed(false);
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => setConfirmOpen(false);

  const handleDownload = async () => {
    setConfirmOpen(false);
    try {
      setLoading(true);
      const response = await fetchExport();
      downloadBlobResponse(response, fallbackFilename);
      setLoading(false);
      onComplete();
    } catch (error) {
      console.error('Failed to download export:', error);
      setLoading(false);
      setErrorOpen(true);
    }
  };

  const userName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.email || '';

  return (
    <>
      <MenuItem onClick={handleConfirmOpen} disabled={loading}>
        <ListItemIcon sx={{ minWidth: 28 }}>
          {loading ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon fontSize="small" />}
        </ListItemIcon>
        <ListItemText primary={label} />
      </MenuItem>
      <StandardDialog
        isOpen={confirmOpen}
        handleClose={handleConfirmClose}
        title="Confirm Export Download"
        maxWidth="sm"
      >
        <Typography>
          This export contains sensitive assessment data and evidence files. Before providing it
          to an <strong>approved AI tool*</strong>, ensure the tool is configured NOT to train on or retain this data.
        </Typography>
        <FormControlLabel
          sx={{ mt: 1 }}
          control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
          label={<>I, <strong>{userName}</strong>, agree</>}
        />
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2 }}>
          * MPA AI Committee have reviewed and approved Microsoft CoPilot under corporate license
          for processing this data.
        </Typography>
        <StandardDialogActions>
          <Button variant="outlined" onClick={handleConfirmClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleDownload} disabled={!agreed}>
            Download
          </Button>
        </StandardDialogActions>
      </StandardDialog>
      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setErrorOpen(false)} severity="error" variant="filled">
          Download failed. Please try again.
        </Alert>
      </Snackbar>
    </>
  );
}
